"use client";

import { useEffect, useRef, useState, ReactNode, CSSProperties } from "react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useMotionMode } from "@/context/MotionContext";

interface MagneticProps {
  children: ReactNode;
  strength?: number;
  tiltStrength?: number;
  padding?: number; // Detection range in pixels
  className?: string;
  style?: CSSProperties;
  as?: "div" | "span";
  onClick?: () => void;
  "data-cursor-ignore"?: boolean;
  disabled?: boolean; // Disable magnetic effect (e.g., during animations)
}

// Default values match CSS tokens: --magnetic-strength, --magnetic-tilt
export function Magnetic({
  children,
  strength = 0.15,
  tiltStrength = 6,
  padding = 160,
  className,
  style,
  as: Component = "div",
  onClick,
  "data-cursor-ignore": dataCursorIgnore,
  disabled = false,
}: MagneticProps) {
  const isMobile = useIsMobile();
  const { mode: motionMode } = useMotionMode();
  const ref = useRef<HTMLDivElement | HTMLSpanElement>(null);
  const [transform, setTransform] = useState({
    x: 0,
    y: 0,
    rotateX: 0,
    rotateY: 0,
  });
  const [isPressed, setIsPressed] = useState(false);

  // Reset transform when motion is turned off or disabled
  useEffect(() => {
    if (motionMode === "off" || disabled) {
      setTransform({ x: 0, y: 0, rotateX: 0, rotateY: 0 });
    }
  }, [motionMode, disabled]);

  useEffect(() => {
    // Skip magnetic effect on mobile, when motion is off, or when disabled
    if (isMobile || motionMode === "off" || disabled) return;
    const el = ref.current;
    if (!el) return;

    // Skip mouse tracking on touch devices - use touch handlers instead
    const isTouchDevice = "ontouchstart" in window;

    const calculateTransform = (clientX: number, clientY: number) => {
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const distX = clientX - centerX;
      const distY = clientY - centerY;

      const isInside =
        clientX >= rect.left - padding &&
        clientX <= rect.right + padding &&
        clientY >= rect.top - padding &&
        clientY <= rect.bottom + padding;

      if (isInside) {
        const rotateY = (distX / (rect.width / 2)) * tiltStrength;
        const rotateX = -(distY / (rect.height / 2)) * tiltStrength;

        setTransform({
          x: distX * strength,
          y: distY * strength,
          rotateX: Math.max(-tiltStrength, Math.min(tiltStrength, rotateX)),
          rotateY: Math.max(-tiltStrength, Math.min(tiltStrength, rotateY)),
        });
      } else {
        setTransform({ x: 0, y: 0, rotateX: 0, rotateY: 0 });
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      // When modal is open, only allow magnetic for elements inside overlays
      if (document.body.style.overflow === "hidden") {
        const isInOverlay = el.closest("[data-case-study], [data-style-guide], [data-manifesto], [data-corner-nav]") !== null;
        if (!isInOverlay) {
          // Only reset if not already at zero to avoid re-render loops
          setTransform(prev =>
            prev.x === 0 && prev.y === 0 && prev.rotateX === 0 && prev.rotateY === 0
              ? prev
              : { x: 0, y: 0, rotateX: 0, rotateY: 0 }
          );
          return;
        }
      }
      calculateTransform(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: Event) => {
      const touchEvent = e as globalThis.TouchEvent;
      if (touchEvent.touches[0]) {
        calculateTransform(touchEvent.touches[0].clientX, touchEvent.touches[0].clientY);
      }
    };

    const handleTouchEnd = () => {
      setTransform({ x: 0, y: 0, rotateX: 0, rotateY: 0 });
    };

    const handleMouseLeave = () => {
      setTransform({ x: 0, y: 0, rotateX: 0, rotateY: 0 });
    };

    if (isTouchDevice) {
      el.addEventListener("touchmove", handleTouchMove, { passive: true });
      el.addEventListener("touchend", handleTouchEnd);
    } else {
      window.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (isTouchDevice) {
        el.removeEventListener("touchmove", handleTouchMove);
        el.removeEventListener("touchend", handleTouchEnd);
      } else {
        window.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, [strength, tiltStrength, padding, isMobile, motionMode, disabled]);

  const isResting = transform.x === 0 && transform.y === 0;

  return (
    <Component
      ref={ref as React.RefObject<HTMLDivElement & HTMLSpanElement>}
      data-magnetic="true"
      data-cursor-ignore={dataCursorIgnore || undefined}
      className={className}
      onClick={onClick}
      onTouchStart={() => setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      onTouchCancel={() => setIsPressed(false)}
      style={{
        ...style,
        display: Component === "span" ? "inline-block" : undefined,
        transform: motionMode === "off"
          ? `scale(${isPressed ? 0.97 : 1})`
          : `perspective(800px) translate(${Math.round(transform.x)}px, ${Math.round(transform.y)}px) rotateX(${Math.round(transform.rotateX)}deg) rotateY(${Math.round(transform.rotateY)}deg) scale(${isPressed ? 0.97 : 1})`,
        transition: motionMode === "off"
          ? "none"
          : isResting
            ? "transform var(--duration-slow) cubic-bezier(0.34, 1.56, 0.64, 1)"
            : "transform var(--duration-fast) ease-out",
        willChange: motionMode === "off" ? undefined : "transform",
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
      }}
    >
      {children}
    </Component>
  );
}
